import React, { useState, useEffect, useRef } from "react";
import { layer_api_path, feature_service_url } from "./api_registry";
import LayerManager from "./components/layerManager";
import NetworkDialog from "./components/networkDialog";
import getGeometryIcon from "./components/utilComponents";

import { getLayerStyle } from "./components/getLayerStyle";
import MapPopupDialog from "./components/mapPopup";

import { fromExtent } from "ol/geom/Polygon";
import { transformExtent } from "ol/proj";
import { fromLonLat } from "ol/proj";
import Select from 'ol/interaction/Select.js';
import { Style, Fill, Stroke, Circle } from 'ol/style.js';
import XYZ from "ol/source/XYZ";

import plugins from "./components/plugins/PluginRepo.js";
import ToolBox from "./components/plugins/ToolBox.js";
import BasemapGallery from "./components/plugins/BaseMapGalary/BaseMapGalary.js";
import AttributeTable from "./components/plugins/AttributeTable/AttributeTable.js";

import { defaults as defaultControls, FullScreen, MousePosition, OverviewMap, ScaleLine, ZoomSlider, ZoomToExtent } from "ol/control";
import { createStringXY } from "ol/coordinate";

import LayerStyleDialog from "./components/LayerStyleDialog.js";
import FeatureInfoButton from "./components/plugins/FeatureInfoButton";
import {buildFlatStyle} from "./components/FlatStyleBuilderUtil.js"


import {
    Box,
    Paper,
    Typography,
    Button,
    Checkbox,
    CircularProgress,
    IconButton,
    Menu,
    MenuItem,
    Alert,
    Dialog,
    DialogTitle,
    DialogContent,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Tooltip
} from "@mui/material";
import {
    Layers,
    MoreVert,
    ZoomIn,
    TableChart,
    Delete,
    Close,

} from "@mui/icons-material";

import ModeEditIcon from '@mui/icons-material/ModeEdit';

import Map from "ol/Map";
import View from "ol/View";
import TileLayer from "ol/layer/Tile";
import OSM from "ol/source/OSM";

import { Vector as VectorLayer } from "ol/layer";
import { Vector as VectorSource } from "ol/source";
import GeoJSON from "ol/format/GeoJSON";
import { bbox as bboxStrategy } from "ol/loadingstrategy";
import { useNavigate } from "react-router-dom";

const geomtypeMap = {
    "POLYGON": "Polygon",
    "LINESTRING": "LineString",
    "POINT": "Point",
}

export default function MapPage({ project_state }) {

    const navigate = useNavigate();
    const [project, setProject] = project_state;

    const mapRef = useRef(null);
    const mapContainerRef = useRef(null);
    const mapInstanceRef = useRef(null);
    const tileLayerRef = useRef(null);

    const [layers, setLayers] = useState({});
    const [loading, setLoading] = useState(false);
    const prevAddedLayersJson = useRef(null);
    const [anchorEl, setAnchorEl] = useState(null);
    const [editorOpen, setEditorOpen] = useState(true);
    const [selectedLayer, setSelectedLayer] = useState(null);
    const [styleVectorLayer, setStyleVectorLayer] = useState(null);

    const [openLayerManageDialog, setOpenLayerManageDialog] = useState(false);
    const [openNetworkRoutingDialog, setOpenNetworkRoutingDialog] =
        useState(false);
    const [popupData, setPopupData] = useState({
        feature: null,
        coordinate: null,
    });
    const [popupOpen, setPopupOpen] = useState(false);
    const overlayRef = useRef(null);

    const [activeBasemapId, setActiveBasemapId] = useState("osm");

    const handleBasemapSelect = (bm) => {
        setActiveBasemapId(bm.id);
        tileLayerRef.current.setSource(
            new XYZ({ url: bm.url })
        );
    };

    // attribute table
    const [tableOpen, setTableOpen] = useState(false);
    const [attrLayer, setAttrLayer] = useState(null);

    const handleAttrOpen = (layer) => {
        setAttrLayer(layer);
        setTableOpen(true);
    };

    const handleAttrClose = () => {
        setAttrLayer(null);
        setTableOpen(false);
    };

    // After transition ends, tell OL to recalculate
    const handleTransitionEnd = () => {
        mapInstanceRef.current?.updateSize();
    };


    //  Fetch all added layers
    const fetchAllAddedLayers = async (project_id, { signal } = {}) => {
        console.log("Fetching layers for project:", project_id);
        try {
            const response = await fetch(
                `${layer_api_path}/${project_id}/added`,
                { method: "GET", headers: { Accept: "application/json" }, signal }
            );
            if (!response.ok) throw new Error(`HTTP ${response.status}`);
            const data = await response.json();
            return data;
        } catch (err) {
            if (err.name === 'AbortError') {
                console.debug('fetchAllAddedLayers aborted');
                return null;
            }
            console.error("Failed to fetch layers:", err);
            return null;
        }
    };

    const handleNewLayerAddingRequest = (layerData) => {
        if (!mapInstanceRef.current || !layerData) return;

        console.log("Adding new layer:", layerData);

        const geomType = layerData.geometry_types?.[0];
        if (!geomType) {
            console.warn("No geometry type found for layer:", layerData);
            return;
        }

        const url = `${feature_service_url}?layer_id=${layerData.id}&geometry_type=${geomType}`;

        const vectorSource = new VectorSource({
            format: new GeoJSON(),
            url: (extent) => `${url}&bbox=${extent.join(",")}`,
            strategy: bboxStrategy,
        });

        const vectorLayer = new VectorLayer({
            source: vectorSource,
            visible: true,
            style: buildFlatStyle(geomType, layerData.style),
        });


        // Mark metadata for later identification
        vectorLayer.set("type", "dynamic");
        vectorLayer.set("geometry_type", geomtypeMap[geomType]);
        vectorLayer.set("title", layerData.name);
        vectorLayer.set("layer_id", layerData.id);

        // Add to map
        mapInstanceRef.current.addLayer(vectorLayer);

        // Attach the OL layer reference to the data
        const updatedLayer = { ...layerData, vectorLayer, vectorSource};

        // Correctly update state with computed key
        setLayers((prev) => ({
            ...prev,
            [updatedLayer.id]: updatedLayer,
        }));

        console.log("Layer successfully added to map and state:", updatedLayer);
    };

    // ---- main effect: fetch added layers when project id changes ----
    useEffect(() => {
        if (!project || !project?.id) {
            // If no project, redirect to projects list
            navigate("/", { replace: true });
            return;
        }

        const controller = new AbortController();

        (async () => {
            setLoading(true);
            try {
                const addedLayers = await fetchAllAddedLayers(project.id, { signal: controller.signal });
                if (!addedLayers) return;

                // lightweight change detection: compare keys (order-independent)
                const keys = Object.keys(addedLayers).sort();
                const keysJson = JSON.stringify(keys);
                if (prevAddedLayersJson.current === keysJson) {
                    console.debug("No change in added layers; skipping heavy map updates.");
                    return;
                }
                prevAddedLayersJson.current = keysJson;

                const map = mapInstanceRef.current;
                if (!map) {
                    console.warn("Map not initialized yet; storing layers state only.");
                    setLayers(addedLayers);
                    return;
                }

                // remove existing dynamic layers before adding new ones
                const existing = map.getLayers && map.getLayers().getArray ? map.getLayers().getArray() : [];
                existing.filter((l) => l.get && l.get("type") === "dynamic").forEach((l) => map.removeLayer(l));

                // iterate and build each vector layer
                for (const [layerId, layerData] of Object.entries(addedLayers).reverse()) {
                    const { id, name, geometry_types } = layerData;
                    const geomType = geometry_types?.[0];
                    if (!geomType) continue;

                    const url = `${feature_service_url}?layer_id=${id}&geometry_type=${geomType}`;

                    const vectorSource = new VectorSource({
                        format: new GeoJSON(),
                        url: (extent) => `${url}&bbox=${extent.join(",")}`,
                        strategy: bboxStrategy,
                    });

                    const vectorLayer = new VectorLayer({
                        source: vectorSource,
                        visible: true,
                        style: buildFlatStyle(geomType, layerData.style),
                    });

                    // tag this as dynamic
                    vectorLayer.set("type", "dynamic");
                    vectorLayer.set("geometry_type", geomtypeMap[geomType]);
                    vectorLayer.set("title", layerData.name);
                    vectorLayer.set("layer_id", layerData.id);

                    // add to map
                    map.addLayer(vectorLayer);

                    // attach to layer object
                    layerData.vectorLayer = vectorLayer;
                    layerData.vectorSource = vectorSource;
                }

                // finally store with OL references
                setLayers(addedLayers);
            } catch (err) {
                if (err.name !== 'AbortError') console.error("Error fetching/adding layers:", err);
            } finally {
                setLoading(false);
            }
        })();

        return () => controller.abort();
    }, [project?.id]);

    //  Initialize map only once
    //  Initialize map only once
    useEffect(() => {
        if (!mapInstanceRef.current && mapRef.current) {
            const tileLayer = new TileLayer({ source: new OSM() });
            tileLayerRef.current = tileLayer;

            mapInstanceRef.current = new Map({
                target: mapRef.current,
                controls: defaultControls({
                    zoom: true,
                    rotate: true,
                    attribution: true,
                }).extend([
                    new ScaleLine({ units: "metric" }),
                ]),
                layers: [tileLayer],
                view: new View({
                    center: [87.38, 21.91],
                    zoom: 12,
                    projection: "EPSG:4326",
                }),
            });

            console.log("Map initialized");
        }
    }, []);

    //  Menu handlers
    const handleMenuOpen = (event, layer_info) => {
        setAnchorEl(event.currentTarget);
        setSelectedLayer(layer_info);
    };

    const handleMenuClose = () => {
        setAnchorEl(null);
        setSelectedLayer(null);
    };

    const handleStylingTool = () => {
        setStyleVectorLayer(selectedLayer);
        handleMenuClose();
    }

    const handleZoomToLayer = () => {
        console.log("Zoom to layer:", selectedLayer);
        const extent = [
            selectedLayer.xmin,
            selectedLayer.ymin,
            selectedLayer.xmax,
            selectedLayer.ymax,
        ];
        const view = mapInstanceRef.current.getView();
        const transformedExtent = transformExtent(
            extent,
            "EPSG:4326",
            view.getProjection()
        );

        view.fit(transformedExtent, {
            duration: 1000, // smooth animation
            padding: [50, 50, 50, 50], // optional padding
        });

        handleMenuClose();
    };

    const handleShowAttribute = () => {
        handleAttrOpen(selectedLayer);
        handleMenuClose();
    };

    const handleLayerRemoved = (layer_id) => {
        console.log("Layer removed from panel:", layer_id);

        const olLayer = layers[layer_id]?.vectorLayer;
        if (olLayer && mapInstanceRef.current) {
            mapInstanceRef.current.removeLayer(olLayer);
        }

        setLayers((prev) => {
            const newLayers = { ...prev };
            delete newLayers[layer_id];
            return newLayers;
        });
    };

    const handleDeleteLayer = () => {
        const layerId = selectedLayer?.id;
        if (!layerId || !mapInstanceRef.current) return;

        console.log("Delete layer:", selectedLayer);

        //  Remove from map
        const olLayer = layers[layerId]?.vectorLayer;
        if (olLayer) {
            mapInstanceRef.current.removeLayer(olLayer);
            console.log(`Removed layer ${layerId} from map`);
        }

        // 3️⃣ Remove from UI layer list
        setLayers((prev) => {
            const newLayers = { ...prev };
            delete newLayers[layerId];
            return newLayers;
        });

        // 4️⃣ Close the menu
        handleMenuClose();
    };

    //  Fix checkbox handler
    const handleLayerVisibility = (layer_id, checked) => {
        console.log(`Layer ${layer_id} visibility: ${checked}`);

        setLayers((prev) => {
            const layer = prev[layer_id];
            console.log(layer);

            // Toggle visibility on the OL map layer if reference exists
            if (layer?.vectorLayer) {
                layer.vectorLayer.setVisible(checked);
            }

            return {
                ...prev,
                [layer_id]: {
                    ...layer,
                    layer_added: checked,
                },
            };
        });
    };

    return (
        <Box
            sx={{
                display: "flex",
                gap: 1,
                height: "100vh",
                width: "100vw",
                overflow: "hidden",
                p: 1,
                boxSizing: "border-box",
            }}
        >
            {/* Sidebar */}
            <Paper
                variant="outlined"
                sx={{
                    width: "25%",
                    display: "flex",
                    flexDirection: "column",
                    flexShrink: 0,
                }}
            >
                <Box
                    sx={{
                        p: 2,
                        borderBottom: 1,
                        borderColor: "divider",
                    }}
                >
                    <Typography variant="h6" fontWeight="600">
                        Layers
                    </Typography>
                </Box>
                {/* layer panel */}
                {loading ? (
                    <Box
                        sx={{
                            flex: 1,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                        }}
                    >
                        <CircularProgress />
                    </Box>
                ) : (
                    <Box sx={{ flex: 1, p: 2, overflowY: "auto" }}>
                        {Object.entries(layers).length === 0 ? (
                            <Box
                                sx={{
                                    textAlign: "center",
                                    color: "text.secondary",
                                    mt: 4,
                                }}
                            >
                                <Layers sx={{ fontSize: 48, opacity: 0.3 }} />
                                <Typography>No layers added</Typography>
                            </Box>
                        ) : (
                            Object.entries(layers).map(
                                ([layer_id, layer_info]) => (
                                    <Box
                                        key={layer_id}
                                        sx={{
                                            display: "flex",
                                            alignItems: "center",
                                            p: 1,
                                            mb: 1,
                                            border: 1,
                                            borderColor: "divider",
                                            borderRadius: 1,
                                        }}
                                    >
                                        <Checkbox
                                            checked={layer_info.layer_added}
                                            onChange={(e) =>
                                                handleLayerVisibility(
                                                    layer_id,
                                                    e.target.checked
                                                )
                                            }
                                            size="small"
                                            sx={{ p: 0, mr: 1 }}
                                        />
                                        <Box sx={{ mr: 1 }}>
                                            {getGeometryIcon(
                                                layer_info.geometry_types[0]
                                            )}
                                        </Box>
                                        <Typography
                                            variant="body2"
                                            sx={{ flex: 1 }}
                                        >
                                            {layer_info.name}
                                        </Typography>
                                        <IconButton
                                            size="small"
                                            onClick={(e) =>
                                                handleMenuOpen(e, layer_info)
                                            }
                                        >
                                            <MoreVert fontSize="small" />
                                        </IconButton>
                                    </Box>
                                )
                            )
                        )}
                    </Box>
                )}

                <Box
                    sx={{
                        p: 1,
                        display: "flex",
                        gap: 1,
                        borderTop: 1,
                        borderColor: "divider",
                    }}
                >
                    <Button
                        variant="contained"
                        fullWidth
                        sx={{ textTransform: "none", fontSize: "0.75rem" }}
                        onClick={() => setOpenLayerManageDialog(true)}
                    >
                        Manage Layers
                    </Button>
                </Box>
            </Paper>

            {/* Map Area */}
            <Paper
                ref={mapContainerRef}
                variant="outlined"
                sx={{
                    width: "75%",
                    height: "100%",
                    position: "relative",
                    overflow: "hidden",
                    display: "flex",
                    flexDirection: "column",
                }}
            >
                {/* Map div — height driven by React state, no ref mutation */}
                <div
                    ref={mapRef}
                    style={{
                        width: "100%",
                        height: tableOpen ? "70%" : "100%",
                        transition: "height 0.25s ease",
                        flexShrink: 0,
                    }}
                    onTransitionEnd={handleTransitionEnd}
                />


                {/* Button stack */}
                <div style={{ position: "absolute", top: 16, right: 16, zIndex: 1300, display: "flex", flexDirection: "column", gap: 8 }}>
                    <ToolBox
                        project={project}
                        tools={plugins}
                        map={mapInstanceRef.current}
                        layers={layers}
                        mapContainer={mapContainerRef.current}
                        onNewLayerCallback={handleNewLayerAddingRequest}
                    />
                    <BasemapGallery
                        selectedId={activeBasemapId}
                        onSelect={handleBasemapSelect}
                    />

                    <FeatureInfoButton 
                        map={mapInstanceRef.current} 
                        mapContainer={mapContainerRef} 
                    />

                </div>
                {/* <FeatureInfoButton map={mapInstanceRef.current} /> */}
                {/* Attribute Table — anchors to bottom of Paper */}
                <AttributeTable
                    layer={attrLayer}
                    map={mapInstanceRef.current}
                    onClose={handleAttrClose}
                />

                {/* <MapPopupDialog
                    feature={popupData.feature}
                    open={popupOpen}
                    onClose={() => setPopupOpen(false)}
                    mapInstance={mapInstanceRef.current}
                /> */}

            </Paper>

            {/* Layer Menu */}
            <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleMenuClose}
            >
                <MenuItem onClick={handleZoomToLayer}>
                    <ZoomIn fontSize="small" sx={{ mr: 1 }} />
                    Zoom to Layer
                </MenuItem>
                <MenuItem onClick={handleShowAttribute}>
                    <TableChart fontSize="small" sx={{ mr: 1 }} />
                    Show Attribute
                </MenuItem>
                <MenuItem onClick={handleStylingTool}>
                    <ZoomIn fontSize="small" sx={{ mr: 1 }} />
                    Styling
                </MenuItem>
                <MenuItem onClick={handleDeleteLayer}>
                    <Delete fontSize="small" sx={{ mr: 1 }} />
                    Delete
                </MenuItem>
            </Menu>

            {/* Dialog for styling  */}
            {styleVectorLayer && (
                <LayerStyleDialog
                    project={project}
                    layerState={styleVectorLayer}
                    onClose={() => setStyleVectorLayer(null)}
                />
            )}

        
            {/* Manage Layers Dialog */}
            <LayerManager
                project={project}
                openPopup={openLayerManageDialog}
                setOpenPopup={setOpenLayerManageDialog}
                onNewLayerCallback={handleNewLayerAddingRequest}
                onLayerRemovedCallback={handleLayerRemoved}
            />


        </Box >
    );
}